/* ============================================================
   Validatoren — geteilte Form-Validierung für Repos und Overlays.
   Jeder Validator: (value, ctx?) → null | { code, message }
   ============================================================ */

export const required = (label = 'Pflichtfeld') => v =>
  (v === null || v === undefined || String(v).trim() === '')
    ? { code: 'required', message: `${label} ist erforderlich` }
    : null

export const maxLength = (n, label = 'Eingabe') => v =>
  (v && String(v).length > n)
    ? { code: 'max_length', message: `${label} darf max. ${n} Zeichen haben` }
    : null

export const dateValidISO = v => {
  if (!v) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return { code: 'date_format', message: 'Datum ist ungültig' }
  if (Number.isNaN(new Date(v).getTime())) return { code: 'date_format', message: 'Datum ist ungültig' }
  return null
}

export const dateNotTooFarPast = (years = 5) => v => {
  if (!v) return null
  const cutoff = new Date()
  cutoff.setFullYear(cutoff.getFullYear() - years)
  return new Date(v) < cutoff
    ? { code: 'date_too_old', message: `Datum darf nicht älter als ${years} Jahre sein` }
    : null
}

export const integerInRange = (min, max, label = 'Zahl') => v => {
  const n = Number(v)
  if (!Number.isInteger(n)) return { code: 'not_integer', message: `${label} muss eine Zahl sein` }
  if (n < min || n > max) return { code: 'out_of_range', message: `${label} muss zwischen ${min} und ${max} liegen` }
  return null
}

export const oneOf = (values, label = 'Wert') => v =>
  values.includes(v) ? null : { code: 'invalid_choice', message: `${label} ist ungültig` }

/* Führt mehrere Validatoren auf demselben Wert aus, gibt den ersten Fehler zurück. */
export const compose = (...validators) => (v, ctx) => {
  for (const fn of validators) {
    const err = fn(v, ctx)
    if (err) return err
  }
  return null
}

/* Validiert ein Objekt anhand eines Schemas { feldname: validatorFn }.
   Gibt { ok: true } oder { ok: false, errors: { feld: 'message' }, first: 'message' } zurück. */
export function validate(obj, schema, ctx) {
  const errors = {}
  let first = null
  for (const key of Object.keys(schema)) {
    const err = schema[key](obj[key], ctx)
    if (err) {
      errors[key] = err.message
      if (!first) first = err.message
    }
  }
  return Object.keys(errors).length
    ? { ok: false, errors, first }
    : { ok: true }
}

/* ── Domain-spezifische Helfer ─────────────────────────────── */

export const jerseyNumber = label =>
  compose(required(label || 'Trikotnummer'), integerInRange(1, 99, label || 'Trikotnummer'))

export const playerPosition = compose(
  required('Position'),
  oneOf(['Tor', 'Rückraum', 'Außen', 'Kreis'], 'Position')
)

export function jerseyUniqueInTeam(players, ignorePlayerId = null) {
  return v => {
    const n = parseInt(v)
    const taken = players.some(p => p.id !== ignorePlayerId && p.jersey_number === n)
    return taken
      ? { code: 'jersey_taken', message: `Trikotnummer ${n} ist bereits vergeben` }
      : null
  }
}
